import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ShieldCheck, Clock, User as UserIcon, CheckCircle2, XCircle } from 'lucide-react';
import { API } from '../config';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'approved', label: 'Approved', icon: CheckCircle2 },
  { key: 'rejected', label: 'Rejected', icon: XCircle },
];

const ApprovalsView = ({ plants, token }) => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [decisionState, setDecisionState] = useState({});

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/edit-requests?status_filter=${statusFilter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (err) {
      alert('Failed to load requests: ' + (err.response?.data?.detail || 'Error'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, token]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const getPlantName = (plantId) => plants.find(p => p.id === plantId)?.name || plantId;

  const handleDecide = async (id, decision) => {
    const note = decisionState[id]?.note || '';
    const hours = parseInt(decisionState[id]?.hours || 24, 10);
    try {
      await axios.post(`${API}/edit-requests/${id}/decide`, {
        status: decision, admin_note: note, access_hours: hours,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setDecisionState(prev => { const next = { ...prev }; delete next[id]; return next; });
      loadRequests();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || 'Error'));
    }
  };

  const updateLocal = (id, patch) =>
    setDecisionState(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6" data-testid="approvals-view">
      <div className="flex items-center mb-4 sm:mb-6">
        <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7 text-[#156ACA] mr-2 flex-shrink-0" />
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Edit Access Approvals</h2>
      </div>

      <div className="flex gap-2 mb-4 sm:mb-6 border-b overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-3 sm:px-4 py-2 min-h-[44px] font-medium text-sm flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              statusFilter === tab.key
                ? 'border-[#156ACA] text-[#156ACA]'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
            data-testid={`approvals-tab-${tab.key}`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-500 text-sm">Loading...</p>}

      {!loading && requests.length === 0 && (
        <p className="text-gray-500 text-sm" data-testid="approvals-empty">
          No {statusFilter} requests.
        </p>
      )}

      <div className="space-y-4">
        {requests.map(req => {
          const local = decisionState[req.id] || {};
          return (
            <div key={req.id} className="border border-gray-200 rounded-lg p-3 sm:p-4" data-testid={`request-${req.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 mb-1 break-words">
                    {getPlantName(req.plant_id)} &middot; {req.date} &middot; Shift {req.shift}
                  </p>
                  <p className="text-sm text-gray-700 mb-2 break-words">&ldquo;{req.reason}&rdquo;</p>
                  <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1">
                      <UserIcon className="h-3 w-3" />
                      {req.requester_username} ({req.requester_role})
                    </span>
                    <span>{new Date(req.created_at).toLocaleString()}</span>
                  </div>
                  {req.status !== 'pending' && (
                    <p className="mt-2 text-xs text-gray-600 break-words">
                      {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.approved_by_username}
                      {req.access_until && ` · access until ${new Date(req.access_until).toLocaleString()}`}
                      {req.admin_note && ` · "${req.admin_note}"`}
                    </p>
                  )}
                </div>

                {req.status === 'pending' && (
                  <div className="flex flex-col gap-2 md:min-w-[220px]">
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder="Access hours (default 24)"
                      value={local.hours ?? ''}
                      onChange={(e) => updateLocal(req.id, { hours: e.target.value })}
                      className="w-full px-3 py-2 min-h-[40px] text-sm border border-gray-300 rounded"
                      data-testid={`access-hours-${req.id}`}
                    />
                    <input
                      type="text"
                      placeholder="Optional note"
                      value={local.note ?? ''}
                      onChange={(e) => updateLocal(req.id, { note: e.target.value })}
                      className="w-full px-3 py-2 min-h-[40px] text-sm border border-gray-300 rounded"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDecide(req.id, 'approved')}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-2 min-h-[40px] rounded"
                        data-testid={`approve-${req.id}`}
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleDecide(req.id, 'rejected')}
                        className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium py-2 min-h-[40px] rounded"
                        data-testid={`reject-${req.id}`}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ApprovalsView;
